/**
 * 我的钱包 Tab 组件 - 积分交易记录
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Paper,
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import pointsApi, { POINT_SOURCE_MAP } from '../../services/pointsApi';

// 兑换按钮呼吸动画：吸引用户注意，引导点击
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.6);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
`;

// 格式化日期
const formatDate = (dateString, format = 'YYYY-MM-DD HH:mm') => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const pad = (n) => String(n).padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());

  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hour)
    .replace('mm', minute);
};

const WalletTab = ({ user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    balance: 0,
    total_earned: 0,
    total_spent: 0,
    growth_level: 1,
    level_name: '新星用户'
  });

  const userId = user?.user_id || user?.id;
  const location = useLocation();
  const history = useHistory();

  // 刷新积分数据
  const refreshData = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      // 获取积分汇总
      const summaryResult = await pointsApi.getSummary(userId);
      if (summaryResult.success) {
        setSummary(summaryResult.data);
      }

      // 获取交易记录
      const type = activeTab === 0 ? null : (activeTab === 1 ? 'earn' : 'spend');
      const txResult = await pointsApi.getTransactions(userId, type, 1, 50);
      if (txResult.success) {
        setTransactions(txResult.data.list || []);
      }
    } catch (err) {
      console.error('获取积分数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 获取积分余额和交易记录
  useEffect(() => {
    refreshData();
  }, [userId, activeTab, location.pathname]);

  // 获取交易类型图标和颜色
  const getTransactionIcon = (item) => {
    if (item.type === 'earn') {
      return <ArrowUpwardIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />;
    }
    return <ArrowDownwardIcon sx={{ fontSize: 20, color: theme.palette.error.main }} />;
  };

  // 获取来源标签
  const getSourceChip = (source) => {
    // 兼容旧数据：source 可能是空对象或字符串
    const sourceStr = typeof source === 'string' ? source : 'other';
    const label = POINT_SOURCE_MAP[sourceStr] || POINT_SOURCE_MAP.other;
    const colorMap = {
      lottery: 'warning',
      watch: 'info',
      signin: 'success',
      recharge: 'primary',
      redeem: 'secondary',
      invite: 'error',
      comment: 'info',
      share: 'info',
      activity: 'warning',
      other: 'default',
    };
    return (
      <Chip
        label={label}
        size="small"
        color={colorMap[sourceStr] || 'default'}
        sx={{ fontSize: 10 }}
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* 积分余额卡片 */}
      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ p: isMobile ? 3 : 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                opacity: 0.9,
              }}
            >
              <AccountBalanceWalletIcon />
              <Typography variant="h6">积分余额</Typography>
            </Box>

            {/* 积分兑换彩票入口 - 跳转到真实彩票领取页面
                胶囊形按钮：图标 + "积分换彩票"文字，更显眼且语义清晰 */}
            <Tooltip title="使用积分兑换真实彩票" arrow placement="left">
              <Box
                component="button"
                onClick={() => history.push('/real-lottery')}
                aria-label="积分兑换彩票"
                sx={{
                  // 重置原生 button 样式
                  border: '2px solid rgba(255, 255, 255, 0.55)',
                  outline: 'none',
                  font: 'inherit',
                  cursor: 'pointer',
                  // 布局
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: isMobile ? 0.75 : 1,
                  px: isMobile ? 1.25 : 1.75,
                  py: isMobile ? 0.75 : 1,
                  borderRadius: 999,
                  // 视觉
                  backgroundColor: 'rgba(255, 255, 255, 0.22)',
                  color: '#fff',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  whiteSpace: 'nowrap',
                  // 动效
                  animation: `${pulseGlow} 2s ease-in-out infinite`,
                  transition: 'transform 0.2s ease, background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.35)',
                    transform: 'scale(1.05)',
                  },
                  '&:active': {
                    transform: 'scale(0.97)',
                  },
                }}
              >
                <ConfirmationNumberIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                <Box component="span" sx={{ lineHeight: 1 }}>
                  积分换彩票
                </Box>
              </Box>
            </Tooltip>
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontSize: isMobile ? 42 : 56,
              fontWeight: 800,
              mb: 1,
            }}
          >
            {summary.balance.toLocaleString()}
          </Typography>

          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {user?.tier?.current_label
              ? `${user.tier.current_label} 级别`
              : `${summary.level_name} · Lv.${summary.growth_level}`}
          </Typography>
        </Box>
      </Paper>

      {/* 统计概览 */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 2,
                  backgroundColor: theme.palette.success.light + '22',
                  borderRadius: 2,
                }}
              >
                <ArrowUpwardIcon
                  sx={{
                    fontSize: 28,
                    color: theme.palette.success.main,
                    mb: 0.5,
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {summary.total_earned.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  累计赚取
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 2,
                  backgroundColor: theme.palette.error.light + '22',
                  borderRadius: 2,
                }}
              >
                <ArrowDownwardIcon
                  sx={{
                    fontSize: 28,
                    color: theme.palette.error.main,
                    mb: 0.5,
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {summary.total_spent.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  累计消耗
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* 交易记录 */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ px: isMobile ? 1 : 2 }}
          >
            <Tab label="全部交易" />
            <Tab label="赚取积分" />
            <Tab label="消费积分" />
          </Tabs>
        </Box>

        <Box sx={{ p: isMobile ? 2 : 3 }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : transactions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                暂无交易记录
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {transactions.map((item, index) => (
                <React.Fragment key={item.transaction_no || item.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem sx={{ py: 1.5 }}>
                    <Box sx={{ mr: 2 }}>
                      {getTransactionIcon(item)}
                    </Box>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            {(() => {
                              // 观看记录拆两行：第1行 "观看剧集"，第2行 "《剧名》 第 X 集"
                              const desc = item.description || '';
                              const isWatch = item.source === 'watch';
                              const isFav = item.source === 'favorite';
                              const watchMatch = isWatch && desc.match(/^观看《(.+?)》\s*第\s+(.+?)\s+集$/);
                              const favMatch = isFav && desc.match(/^收藏《(.+?)》$/);
                              if (watchMatch) {
                                const [, dramaName] = watchMatch;
                                return (
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      观看剧集
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: 'text.secondary', mt: 0.25, wordBreak: 'break-word' }}
                                    >
                                      《{dramaName}》
                                    </Typography>
                                  </Box>
                                );
                              }
                              if (favMatch) {
                                const [, dramaName] = favMatch;
                                return (
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      收藏剧集
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: 'text.secondary', mt: 0.25, wordBreak: 'break-word' }}
                                    >
                                      《{dramaName}》
                                    </Typography>
                                  </Box>
                                );
                              }
                              return (
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {desc || (item.type === 'earn' ? '积分赚取' : '积分消费')}
                                </Typography>
                              );
                            })()}
                            {getSourceChip(item.source)}
                          </Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color:
                                item.type === 'earn'
                                  ? theme.palette.success.main
                                  : theme.palette.error.main,
                            }}
                          >
                            {item.type === 'earn' ? '+' : '-'} {item.amount.toLocaleString()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box
                          sx={{
                            mt: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(item.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default WalletTab;
